import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { REVIEWS as SEED_REVIEWS } from "@/data/reviews";
import { reviewsApi } from "@/lib/api";

const strongestLine = (r) => (r.pull || r.quote || "").trim();

/** Review Wall Highlights: the strongest lines (admin-curated pull quotes)
 *  from published member reviews, pulled onto the landing page automatically.
 *  Seeds from the prerender snapshot (or the committed registry) so the
 *  server-rendered page is never empty, then refreshes from /api/reviews on
 *  hydration - a review published in admin appears here with no code change. */
export const ReviewHighlights = () => {
  const [reviews, setReviews] = useState(
    () => globalThis.__W3RC_PRERENDER_DATA__?.reviews || SEED_REVIEWS,
  );
  useEffect(() => {
    let alive = true;
    reviewsApi
      .list()
      .then((list) => {
        if (alive && Array.isArray(list) && list.length) setReviews(list);
      })
      .catch(() => {}); // seed content stands if the fetch fails
    return () => {
      alive = false;
    };
  }, []);

  const items = (reviews || []).filter((r) => strongestLine(r)).slice(0, 3);
  if (!items.length) return null;
  const [featured, ...more] = items;

  return (
    <div data-testid="review-highlights">
      <div className="flex items-center gap-0.5 mb-4" aria-label="Rated 5 of 5 by members">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} size={16} strokeWidth={1.5} className="text-[#FF6900] fill-[#FF6900]" />
        ))}
      </div>
      <blockquote
        data-testid="review-highlight-featured"
        className="font-serif text-2xl lg:text-3xl tracking-tight leading-[1.25] text-ink max-w-3xl"
      >
        &ldquo;{strongestLine(featured)}&rdquo;
      </blockquote>
      <div className="font-mono text-xs text-ink/85 mt-3">
        {featured.name}
        {featured.title ? <> &nbsp;·&nbsp; {featured.title}</> : null}
        {featured.badge ? <> &nbsp;·&nbsp; {featured.badge}</> : null}
      </div>

      {more.length > 0 && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
          {more.map((r) => (
            <figure
              key={r.id}
              data-testid={`review-highlight-${r.id}`}
              className="border-l-2 border-blueprint pl-4"
            >
              <blockquote className="font-serif text-lg leading-snug text-ink">
                &ldquo;{strongestLine(r)}&rdquo;
              </blockquote>
              <figcaption className="font-mono text-[11px] text-ink/75 mt-2 leading-snug">
                {r.name}
                {r.title ? <> &nbsp;·&nbsp; {r.title}</> : null}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
};
